import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errMsg, fmtMoney, fmtQty, fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, CheckCircle2, AlertTriangle } from "lucide-react";
import { ExportButtons } from "../components/ExportButtons";

export default function Match() {
  const { t } = useI18n();
  const { can, user } = useAuth();
  const [pos, setPos] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [poNumber, setPoNumber] = useState("");
  const [match, setMatch] = useState(null);
  const [open, setOpen] = useState(false);
  const [invForm, setInvForm] = useState({ po_number: "", supplier_invoice_number: "", lines: [] });
  const [searchParams] = useSearchParams();
  const openId = searchParams.get("open");

  const load = useCallback(() => {
    api.get("/purchase-orders").then((r) => setPos(r.data.filter((p) => ["open", "partial", "received"].includes(p.status)))).catch(() => {});
    api.get("/invoices").then((r) => {
      setInvoices(r.data);
      if (openId) {
        const inv = r.data.find((i) => i.id === openId);
        if (inv) setPoNumber(inv.po_number);
        else toast.error("Factura no encontrada");
      }
    }).catch((e) => toast.error(errMsg(e)));
  }, [openId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (poNumber) api.get(`/match/${poNumber}`).then((r) => setMatch(r.data)).catch(() => setMatch(null));
    else setMatch(null);
  }, [poNumber, invoices]);

  const openInvoiceDialog = (o) => {
    setOpen(o);
    if (o && poNumber) {
      const po = pos.find((p) => p.number === poNumber);
      setInvForm({ po_number: poNumber, supplier_invoice_number: "",
        lines: po ? po.lines.map((l) => ({ item_number: l.item_number, qty: l.received_qty || l.qty, unit_cost: l.unit_cost })) : [] });
    }
  };

  const saveInvoice = async () => {
    try {
      await api.post("/invoices", { ...invForm, lines: invForm.lines.map((l) => ({ item_number: l.item_number, qty: Number(l.qty), unit_cost: Number(l.unit_cost) })) });
      toast.success(t("common.created"));
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const invoiceAction = async (id, act, force = false) => {
    try {
      await api.post(`/invoices/${id}/${act}`, null, { params: force ? { force: true } : {} });
      toast.success(t(act === "approve" ? "mt.invoiceApproved" : "mt.invoiceRejected"));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const poInvoices = invoices.filter((i) => !poNumber || i.po_number === poNumber);

  return (
    <div data-testid="match-page">
      <PageHeader title={t("mt.title")} subtitle={t("mt.subtitle")}>
        {poNumber && can("admin", "buyer", "approver", "warehouse") && <ExportButtons path={`/reports/match/${poNumber}`} filename={`match_${poNumber}`} testid="export-match" />}
        {can("admin", "buyer") && poNumber && (
          <Dialog open={open} onOpenChange={openInvoiceDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-none text-xs font-bold" data-testid="invoice-new-button">
                <Plus size={13} className="mr-1" /> {t("mt.newInvoice")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:max-w-xl">
              <DialogHeader>
                <DialogTitle className="uppercase font-black">{t("mt.newInvoice")} — {poNumber}</DialogTitle>
                <DialogDescription className="text-xs">{t("mt.subtitle")}</DialogDescription>
              </DialogHeader>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mt.supplierInvoice")}</Label>
                <Input value={invForm.supplier_invoice_number}
                  onChange={(e) => setInvForm({ ...invForm, supplier_invoice_number: e.target.value })}
                  className="rounded-none h-8 text-xs" data-testid="invoice-number-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.lines")}</Label>
                {invForm.lines.map((l, i) => (
                  <div key={i} className="flex gap-2 items-center text-xs">
                    <span className="flex-1 font-bold">{l.item_number}</span>
                    <Input type="number" value={l.qty} onChange={(e) => {
                      const lines = [...invForm.lines]; lines[i] = { ...l, qty: e.target.value }; setInvForm({ ...invForm, lines });
                    }} className="rounded-none h-7 text-xs w-24" data-testid={`invoice-line-qty-${i}`} />
                    <Input type="number" value={l.unit_cost} onChange={(e) => {
                      const lines = [...invForm.lines]; lines[i] = { ...l, unit_cost: e.target.value }; setInvForm({ ...invForm, lines });
                    }} className="rounded-none h-7 text-xs w-28" data-testid={`invoice-line-cost-${i}`} />
                  </div>
                ))}
              </div>
              <Button onClick={saveInvoice} disabled={!invForm.supplier_invoice_number}
                className="rounded-none font-bold" data-testid="invoice-save-button">{t("common.create")}</Button>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="mb-4">
        <Select value={poNumber} onValueChange={setPoNumber}>
          <SelectTrigger className="rounded-none h-9 text-xs w-80" data-testid="match-po-select">
            <SelectValue placeholder={t("rc.selectPo")} />
          </SelectTrigger>
          <SelectContent>{pos.map((p) => <SelectItem key={p.id} value={p.number}>{p.number} — {p.supplier_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {match && (
        <>
          <div className={`border p-3 mb-4 flex items-center gap-2 text-xs font-bold ${match.match_ok
            ? "border-green-400 bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-300"
            : "border-red-400 bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-300"}`}
            data-testid="match-result-banner">
            {match.match_ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            {match.match_ok ? t("mt.matchOk") : t("mt.matchFail")}
          </div>
          <div className="tbl-wrap mb-6" data-testid="match-table">
            <table className="tbl">
              <thead><tr>
                <th>{t("common.item")}</th><th>{t("common.description")}</th>
                <th className="num bg-secondary">{t("mt.poQty")}</th><th className="num bg-secondary">{t("mt.poCost")}</th>
                <th className="num">{t("mt.rcQty")}</th>
                <th className="num bg-secondary">{t("mt.invQty")}</th><th className="num bg-secondary">{t("mt.invCost")}</th>
                <th>{t("common.status")}</th>
              </tr></thead>
              <tbody>
                {match.lines.map((l) => (
                  <tr key={l.item_number}>
                    <td className="font-bold">{l.item_number}</td>
                    <td>{l.description}</td>
                    <td className="num">{fmtQty(l.po_qty)}</td>
                    <td className="num">{fmtMoney(l.po_cost)}</td>
                    <td className={`num font-bold ${l.received_qty !== l.invoiced_qty ? "text-destructive" : ""}`}>{fmtQty(l.received_qty)}</td>
                    <td className={`num font-bold ${l.received_qty !== l.invoiced_qty ? "text-destructive" : ""}`}>{fmtQty(l.invoiced_qty)}</td>
                    <td className={`num font-bold ${l.invoiced_cost != null && Math.abs(l.invoiced_cost - l.po_cost) > 0.01 ? "text-destructive" : ""}`}>
                      {l.invoiced_cost != null ? fmtMoney(l.invoiced_cost) : "—"}</td>
                    <td><StatusBadge status={l.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("mt.invoices")}</div>
      {poInvoices.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="invoices-table">
          <table className="tbl">
            <thead><tr>
              <th>{t("common.number")}</th><th>{t("mt.supplierInvoice")}</th><th>OC</th><th>{t("common.supplier")}</th>
              <th className="num">{t("common.total")}</th><th>{t("common.status")}</th><th>{t("common.date")}</th><th>{t("common.actions")}</th>
            </tr></thead>
            <tbody>
              {poInvoices.map((inv) => (
                <tr key={inv.id} className={inv.id === openId ? "bg-amber-50 dark:bg-amber-950/30" : ""} data-testid={`invoice-row-${inv.internal_number}`}>
                  <td className="font-bold">{inv.internal_number}</td>
                  <td>{inv.supplier_invoice_number}</td>
                  <td><button className="text-primary font-bold hover:underline" onClick={() => setPoNumber(inv.po_number)}
                    data-testid={`invoice-po-link-${inv.internal_number}`}>{inv.po_number}</button></td>
                  <td>{inv.supplier_name}</td>
                  <td className="num font-bold">{fmtMoney(inv.total)}</td>
                  <td><StatusBadge status={inv.status} /></td>
                  <td className="text-muted-foreground">{fmtDate(inv.created_at)}</td>
                  <td>
                    {inv.status === "pending" && can("admin", "approver") && (
                      <div className="flex gap-1.5">
                        <button onClick={() => invoiceAction(inv.id, "approve")} data-testid={`invoice-approve-${inv.internal_number}`}
                          className="border border-green-500 text-green-700 dark:text-green-400 px-2 py-0.5 text-[10px] font-bold uppercase hover:bg-green-50 dark:hover:bg-green-950">
                          {t("mt.approvePay")}
                        </button>
                        {user?.role === "admin" && (
                          <button onClick={() => invoiceAction(inv.id, "approve", true)} data-testid={`invoice-force-${inv.internal_number}`}
                            className="border border-amber-500 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase hover:bg-amber-50 dark:hover:bg-amber-950">
                            {t("mt.forceApprove")}
                          </button>
                        )}
                        <button onClick={() => invoiceAction(inv.id, "reject")} data-testid={`invoice-reject-${inv.internal_number}`}
                          className="border border-red-500 text-red-700 dark:text-red-400 px-2 py-0.5 text-[10px] font-bold uppercase hover:bg-red-50 dark:hover:bg-red-950">
                          {t("common.reject")}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
